import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import {
	CreateVestingTermDto,
	CreateVestingTermGradedDto,
	CreateVestingTermLeaverProvisionDto,
	CreateVestingTermStraightLineDto,
	UpdateVestingTermDto,
	UpdateVestingTermGradedDto,
	UpdateVestingTermLeaverProvisionDto,
	UpdateVestingTermStraightLineDto,
	VestingTermGradedRes,
	VestingTermLeaverProvisionRes,
	VestingTermRes,
	VestingTermSimplifiedRes,
	VestingTermStraightLineRes,
} from '@dealstack/shared/lib/generated';

export const getOrganizationVestingTerms = async (organizationId: string, signal?: AbortSignal) => {
	return await makeRequest<ApiV1.Response<VestingTermRes[]>>(
		{
			endpoint: formatUnicorn(API_PATHS.PROGRAMME_DEFAULTS_VESTING_TERM_ORGANIZATIONID_GET, {
				organizationId,
			}),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	);
};

export const createOrganizationVestingTerm = async (
	organizationId: string,
	args: CreateVestingTermDto
) => {
	return await makeRequest<ApiV1.Response<VestingTermSimplifiedRes>>({
		endpoint: formatUnicorn(API_PATHS.PROGRAMME_DEFAULTS_VESTING_TERM_ORGANIZATIONID_POST, {
			organizationId,
		}),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	});
};

export const updateOrganizationVestingTerm = async (
	organizationId: string,
	args: { id: string } & UpdateVestingTermDto
) => {
	return await makeRequest<ApiV1.Response<VestingTermSimplifiedRes>>({
		endpoint: formatUnicorn(
			API_PATHS.PROGRAMME_DEFAULTS_VESTING_TERM_ORGANIZATIONID_VESTINGTERMID_PATCH,
			{ organizationId, vestingTermId: args.id }
		),
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify(args),
	});
};

export const deleteOrganizationVestingTerm = async (
	organizationId: string,
	args: { id: string }
) => {
	await makeRequest({
		endpoint: formatUnicorn(
			API_PATHS.PROGRAMME_DEFAULTS_VESTING_TERM_ORGANIZATIONID_VESTINGTERMID_DELETE,
			{ organizationId, vestingTermId: args.id }
		),
		method: HTTP_METHOD.DELETE,
	});
};

export const createOrganizationVestingTermStraightLine = async (
	organizationId: string,
	args: CreateVestingTermStraightLineDto
) => {
	return await makeRequest<ApiV1.Response<VestingTermStraightLineRes>>({
		endpoint: formatUnicorn(
			API_PATHS.PROGRAMME_DEFAULTS_VESTING_TERM_ORGANIZATIONID_STRAIGHT_LINE_POST,
			{ organizationId }
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	});
};

export const updateOrganizationVestingTermStraightLine = async (
	organizationId: string,
	args: { straightLineTermId: string } & UpdateVestingTermStraightLineDto
) => {
	return await makeRequest<ApiV1.Response<VestingTermStraightLineRes>>({
		endpoint: formatUnicorn(
			API_PATHS.PROGRAMME_DEFAULTS_VESTING_TERM_ORGANIZATIONID_STRAIGHT_LINE_STRAIGHTLINEID_PUT,
			{ organizationId, straightLineId: args.straightLineTermId }
		),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(args),
	});
};

export const createOrganizationVestingTermGraded = async (
	organizationId: string,
	args: CreateVestingTermGradedDto
) => {
	return await makeRequest<ApiV1.Response<VestingTermGradedRes>>({
		endpoint: formatUnicorn(
			API_PATHS.PROGRAMME_DEFAULTS_VESTING_TERM_ORGANIZATIONID_GRADED_POST,
			{ organizationId }
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	});
};

export const updateOrganizationVestingTermGraded = async (
	organizationId: string,
	args: { gradedTermId: string } & UpdateVestingTermGradedDto
) => {
	return await makeRequest<ApiV1.Response<VestingTermGradedRes>>({
		endpoint: formatUnicorn(
			API_PATHS.PROGRAMME_DEFAULTS_VESTING_TERM_ORGANIZATIONID_GRADED_GRADEDID_PUT,
			{ organizationId, gradedId: args.gradedTermId }
		),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(args),
	});
};

export const deleteOrganizationVestingTermGraded = async (
	organizationId: string,
	args: { gradedTermId: string }
) => {
	await makeRequest({
		endpoint: formatUnicorn(
			API_PATHS.PROGRAMME_DEFAULTS_VESTING_TERM_ORGANIZATIONID_GRADED_GRADEDID_DELETE,
			{ organizationId, gradedId: args.gradedTermId }
		),
		method: HTTP_METHOD.DELETE,
	});
};

export const createOrganizationVestingTermLeaverProvision = async (
	organizationId: string,
	args: CreateVestingTermLeaverProvisionDto
) => {
	return await makeRequest<ApiV1.Response<VestingTermLeaverProvisionRes>>({
		endpoint: formatUnicorn(
			API_PATHS.PROGRAMME_DEFAULTS_VESTING_TERM_ORGANIZATIONID_LEAVER_PROVISION_POST,
			{ organizationId }
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	});
};

export const updateOrganizationVestingTermLeaverProvision = async (
	organizationId: string,
	args: { leaverProvisionId: string } & UpdateVestingTermLeaverProvisionDto
) => {
	return await makeRequest<ApiV1.Response<VestingTermLeaverProvisionRes>>({
		endpoint: formatUnicorn(
			API_PATHS.PROGRAMME_DEFAULTS_VESTING_TERM_ORGANIZATIONID_LEAVER_PROVISION_LEAVERPROVISIONID_PUT,
			{ organizationId, leaverProvisionId: args.leaverProvisionId }
		),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(args),
	});
};

export const deleteOrganizationVestingTermLeaverProvision = async (
	organizationId: string,
	args: { leaverProvisionId: string }
) => {
	await makeRequest({
		endpoint: formatUnicorn(
			API_PATHS.PROGRAMME_DEFAULTS_VESTING_TERM_ORGANIZATIONID_LEAVER_PROVISION_LEAVERPROVISIONID_DELETE,
			{ organizationId, leaverProvisionId: args.leaverProvisionId }
		),
		method: HTTP_METHOD.DELETE,
	});
};
